import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardSidebar from '@/components/feature/DashboardSidebar';
import DashboardTopBar from '@/components/feature/DashboardTopBar';
import StatusBadge from '@/components/base/StatusBadge';
import { ORGANIZER_RESERVATIONS } from '@/mocks/organizer';
import type { Event, EventTicketType } from '@/mocks/admin';
import OrganizerCharts from './components/OrganizerCharts';
import FinancialDashboard from './components/FinancialDashboard';
import EventDetailPanel from './components/EventDetailPanel';
import TicketDetailPanel from './components/TicketDetailPanel';
import type { Reservation } from '@/mocks/organizer';
import { useEventsStore } from '@/hooks/useEventsStore';
import { useNotificationsStore, saveLastSeenDate } from '@/hooks/useNotificationsStore';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/lib/api';
import { EVENT_CATEGORIES } from '@/constants/eventCategories';
import type { Notification } from '@/mocks/shared';

type Tab = 'overview' | 'events' | 'tickets' | 'finances' | 'notifications' | 'add' | 'profile';
type Step = 1 | 2 | 3;

const ORGANIZER_TAB_PATH_MAP: Record<Exclude<Tab, 'add'>, string> = {
  overview: '/organizer',
  events: '/organizer/events',
  tickets: '/organizer/tickets',
  finances: '/organizer/finances',
  notifications: '/organizer/notifications',
  profile: '/organizer/profile',
};

const getOrganizerTabFromPath = (pathname: string): Tab => {
  if (pathname.includes('/profile')) return 'profile';
  if (pathname.includes('/events')) return 'events';
  if (pathname.includes('/tickets')) return 'tickets';
  if (pathname.includes('/finances')) return 'finances';
  if (pathname.includes('/notifications')) return 'notifications';
  return 'overview';
};

const formatPrice = (n: number) =>
  n === 0 ? 'Gratuit' : `${n.toLocaleString('fr-FR')} FCFA`;

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

interface EventForm {
  title: string; type: string; date: string; endDate: string; location: string;
  diocese: string; description: string; ticketPrice: string; totalSeats: string;
  ticketTypes: TicketTypeForm[];
  organizerName: string; contact: string; externalLink: string;
}
interface TicketTypeForm {
  id?: string;
  name: string;
  price: string;
  seats: string;
  description: string;
}
const emptyForm: EventForm = {
  title: '', type: '', date: '', endDate: '', location: '', diocese: '', description: '',
  ticketPrice: '0', totalSeats: '', organizerName: '',
  ticketTypes: [{ name: 'Standard', price: '0', seats: '', description: '' }],
  contact: '', externalLink: '',
};

const normalizedTicketTypes = (ticketTypes: TicketTypeForm[]): EventTicketType[] => (
  ticketTypes
    .filter((ticketType) => ticketType.name.trim())
    .map((ticketType) => ({
      id: ticketType.id,
      name: ticketType.name.trim(),
      description: ticketType.description.trim(),
      price: Number(ticketType.price || 0),
      seats: ticketType.seats === '' ? null : Number(ticketType.seats),
    }))
);

const ticketSummary = (ticketTypes: TicketTypeForm[]) => {
  const normalized = normalizedTicketTypes(ticketTypes);
  const prices = normalized.map((ticketType) => ticketType.price);
  const seats = normalized.reduce((sum, ticketType) => sum + Number(ticketType.seats ?? 0), 0);

  return {
    ticketTypes: normalized,
    ticketPrice: prices.length ? Math.min(...prices) : 0,
    totalSeats: seats,
  };
};

const cleanProfileValue = (value?: string | null) => (value ?? '').trim();
const organizationNameFromUser = (user?: {
  name?: string | null;
  vendor_application?: { shop_name?: string | null } | null;
} | null) => cleanProfileValue(user?.vendor_application?.shop_name) || cleanProfileValue(user?.name) || 'Organisateur';

const profileFromUser = (user?: NonNullable<ReturnType<typeof useAuth>['user']>) => ({
  name: user?.name ?? '',
  email: user?.email ?? '',
  phone: user?.phone ?? '',
  location: user?.vendor_application?.location ?? user?.paroisse ?? user?.diocese_prefere ?? '',
  avatar: user?.avatar ?? '',
  structureName: user?.vendor_application?.shop_name ?? '',
  eventCategory: user?.vendor_application?.category ?? '',
  bio: user?.vendor_application?.bio ?? '',
});

interface ProfileData {
  name: string; email: string; phone: string; location: string;
  avatar: string; structureName: string; eventCategory: string; bio: string;
}

export default function OrganizerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [tab, setTabState] = useState<Tab>(() => getOrganizerTabFromPath(location.pathname));

  const setTab = (newTab: Tab) => {
    setTabState(newTab);
    if (newTab !== 'add' && newTab in ORGANIZER_TAB_PATH_MAP) {
      navigate(ORGANIZER_TAB_PATH_MAP[newTab as keyof typeof ORGANIZER_TAB_PATH_MAP]);
    }
  };

  useEffect(() => {
    const urlTab = getOrganizerTabFromPath(location.pathname);
    setTabState(urlTab);
  }, [location.pathname]);

  // ── Events State (shared store with admin) ──
  const { addEvent, updateEvent, deleteEvent, restoreEvent, events: allEvents } = useEventsStore();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Reservation | null>(null);
  const [editEvent, setEditEvent] = useState<Event | null>(null);
  const [deleteEventTarget, setDeleteEventTarget] = useState<Event | null>(null);
  const [editEventForm, setEditEventForm] = useState({
    title: '',
    type: '',
    date: '',
    endDate: '',
    location: '',
    diocese: '',
    ticketPrice: '',
    totalSeats: '',
    ticketTypes: [{ name: 'Standard', price: '0', seats: '', description: '' }] as TicketTypeForm[],
    image: '',
    description: '',
  });
  const [reservationsState, setReservationsState] = useState<Reservation[]>(ORGANIZER_RESERVATIONS);

  // ── Events tab search/filter ──
  const [eventSearch, setEventSearch] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'deleted'>('all');

  // ── Tickets tab search/filter ──
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState<'all' | 'confirmed' | 'pending' | 'cancelled'>('all');

  const events = useMemo(() => allEvents.map((event) => ({
    ...event,
    soldSeats: reservationsState
      .filter((reservation) => reservation.eventId === event.id && reservation.status === 'confirmed')
      .reduce((sum, reservation) => sum + reservation.tickets, 0),
  })), [allEvents, reservationsState]);

  useEffect(() => {
    const loadOrganizerTickets = async () => {
      try {
        const response = await apiRequest<Array<{
          id: string;
          event_id: string;
          event_title: string;
          buyer: string;
          tickets: number;
          total: number;
          status: Reservation['status'];
          date: string;
          ticket_code: string;
          email: string;
          phone: string;
          address?: string | null;
        }>>('/organizer/tickets');

        setReservationsState(response.map((ticket) => ({
          id: ticket.id,
          eventId: ticket.event_id,
          eventTitle: ticket.event_title,
          buyer: ticket.buyer,
          tickets: ticket.tickets,
          total: Number(ticket.total ?? 0),
          status: ticket.status,
          date: ticket.date,
          ticketCode: ticket.ticket_code,
          email: ticket.email,
          phone: ticket.phone,
          address: ticket.address || undefined,
        })));
      } catch (error) {
        console.error('Organizer tickets error:', error);
      }
    };

    void loadOrganizerTickets();
  }, []);

  // ── Derived: filtered events ──
  const eventStatusCounts = {
    all: events.length,
    pending: events.filter(e => e.status === 'pending').length,
    approved: events.filter(e => e.status === 'approved').length,
    rejected: events.filter(e => e.status === 'rejected').length,
    deleted: events.filter(e => e.status === 'deleted').length,
  };
  const filteredEvents = events.filter((e) => {
    const q = eventSearch.toLowerCase();
    const matchSearch = !q || e.title.toLowerCase().includes(q) || e.type.toLowerCase().includes(q) || e.location.toLowerCase().includes(q);
    const matchStatus = eventStatusFilter === 'all' || e.status === eventStatusFilter;
    return matchSearch && matchStatus;
  });

  // ── Derived: filtered tickets ──

  const ticketStatusCounts = {
    all: reservationsState.length,
    confirmed: reservationsState.filter(r => r.status === 'confirmed').length,
    pending: reservationsState.filter(r => r.status === 'pending').length,
    cancelled: reservationsState.filter(r => r.status === 'cancelled').length,
  };
  const filteredTickets = reservationsState.filter((r) => {
    const q = ticketSearch.toLowerCase();
    const matchSearch = !q || r.ticketCode.toLowerCase().includes(q) || r.buyer.toLowerCase().includes(q) || r.eventTitle.toLowerCase().includes(q);
    const matchStatus = ticketStatusFilter === 'all' || r.status === ticketStatusFilter;
    return matchSearch && matchStatus;
  });

  // ── Notifications Store ──
  const { notifications, unreadCount, markAsRead, markAllAsRead, newSinceLastSeen } = useNotificationsStore('organizer');
  const [newNotifToast, setNewNotifToast] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  useEffect(() => {
    if (newSinceLastSeen.length > 0) {
      const first = newSinceLastSeen[0];
      setNewNotifToast(first.type === 'approval'
        ? `Événement approuvé — ${first.message}`
        : `Événement rejeté — voir les notifications`
      );
      const t = setTimeout(() => setNewNotifToast(null), 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [newSinceLastSeen]);

  // Auto mark-as-read when notifications tab is open
  useEffect(() => {
    if (tab === 'notifications' && unreadCount > 0) {
      const t = setTimeout(() => { markAllAsRead(); saveLastSeenDate(); }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [markAllAsRead, tab, unreadCount]);

  // ── Form state ──
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<EventForm>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof EventForm, string>>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitSaving, setSubmitSaving] = useState(false);

  // ── Profile state ──
  const [profile, setProfile] = useState<ProfileData>({
    ...profileFromUser(user ?? undefined),
  });
  const [profileEdit, setProfileEdit] = useState<ProfileData>({ ...profile });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileToast, setProfileToast] = useState<string | null>(null);
  const [profileAvatarFile, setProfileAvatarFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const eventImageInputRef = useRef<HTMLInputElement>(null);
  const editEventImageInputRef = useRef<HTMLInputElement>(null);
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState('');
  const [editEventImageFile, setEditEventImageFile] = useState<File | null>(null);
  const formTicketSummary = ticketSummary(form.ticketTypes);
  const isPaidEvent = formTicketSummary.ticketTypes.some((ticketType) => ticketType.price > 0);
  const editTicketSummary = ticketSummary(editEventForm.ticketTypes);
  const isEditPaidEvent = editTicketSummary.ticketTypes.some((ticketType) => ticketType.price > 0);

  useEffect(() => {
    if (!user) return;
    const nextProfile = profileFromUser(user);
    const structureName = nextProfile.structureName || organizationNameFromUser(user);

    setProfile(nextProfile);
    setProfileEdit(nextProfile);
    setForm((current) => {
      const currentOrganizerName = cleanProfileValue(current.organizerName);
      const userName = cleanProfileValue(user.name);
      const shouldUseStructureName = structureName
        && (!currentOrganizerName || currentOrganizerName === userName || currentOrganizerName === 'Organisateur');

      if (!shouldUseStructureName && currentOrganizerName) {
        return current;
      }

      return { ...current, organizerName: structureName, contact: user.phone ?? current.contact };
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const refreshProfile = async () => {
      try {
        const freshUser = await apiRequest<NonNullable<typeof user>>('/user');
        if (cancelled) return;
        updateUser(freshUser);
      } catch (error) {
        console.error('Organizer profile refresh error:', error);
      }
    };

    void refreshProfile();

    return () => {
      cancelled = true;
    };
  }, [updateUser, user?.id]);

  const totalTicketsSold = useMemo(() => reservationsState
    .filter((reservation) => reservation.status === 'confirmed')
    .reduce((sum, reservation) => sum + reservation.tickets, 0), [reservationsState]);
  const totalRevenue = useMemo(() => reservationsState.filter((reservation) => reservation.status === 'confirmed').reduce((sum, reservation) => sum + reservation.total, 0), [reservationsState]);
  const currentMonthRevenue = useMemo(() => {
    const now = new Date();
    return reservationsState
      .filter((reservation) => {
        const reservationDate = new Date(reservation.date);
        return reservation.status === 'confirmed'
          && reservationDate.getMonth() === now.getMonth()
          && reservationDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, reservation) => sum + reservation.total, 0);
  }, [reservationsState]);
  const avgFillRate = useMemo(() => {
    const withCapacity = events.filter((event) => event.totalSeats > 0);
    if (withCapacity.length === 0) return 0;
    return Math.round(withCapacity.reduce((sum, event) => sum + fillRate(event.soldSeats, event.totalSeats), 0) / withCapacity.length);
  }, [events]);

  const ticketsMonthly = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (6 - index));
      const month = date.toLocaleDateString('fr-FR', { month: 'short' });
      const matchingReservations = reservationsState.filter((reservation) => {
        const reservationDate = new Date(reservation.date);
        return reservationDate.getMonth() === date.getMonth() && reservationDate.getFullYear() === date.getFullYear();
      });

      return {
        month: month.charAt(0).toUpperCase() + month.slice(1, 3),
        revenus: matchingReservations.filter((reservation) => reservation.status === 'confirmed').reduce((sum, reservation) => sum + reservation.total, 0),
        tickets: matchingReservations.filter((reservation) => reservation.status === 'confirmed').reduce((sum, reservation) => sum + reservation.tickets, 0),
      };
    })
  ), [reservationsState]);

  const fillRateByEvent = useMemo(() => (
    events.slice(0, 6).map((event) => ({
      event: event.title.slice(0, 10),
      taux: fillRate(event.soldSeats, event.totalSeats),
    }))
  ), [events]);

  const revenueByType = useMemo(() => {
    const palette = ['#C9A84C', '#1B2A4A', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];
    const map = events.reduce<Record<string, number>>((accumulator, event) => {
      const eventRevenue = reservationsState
        .filter((reservation) => reservation.eventId === event.id && reservation.status === 'confirmed')
        .reduce((sum, reservation) => sum + reservation.total, 0);
      accumulator[event.type] = (accumulator[event.type] ?? 0) + eventRevenue;
      return accumulator;
    }, {});

    return Object.entries(map).map(([type, revenus], index) => ({
      type,
      revenus,
      fill: palette[index % palette.length],
    }));
  }, [events, reservationsState]);

  const revenueByEvent = useMemo(() => (
    events
      .map((event) => {
        const eventReservations = reservationsState.filter((reservation) => reservation.eventId === event.id);
        const confirmedReservations = eventReservations.filter((reservation) => reservation.status === 'confirmed');
        const revenue = confirmedReservations.reduce((sum, reservation) => sum + reservation.total, 0);
        const tickets = confirmedReservations.reduce((sum, reservation) => sum + reservation.tickets, 0);
        const eventDate = new Date(event.date);

        return {
          id: event.id,
          event: event.title,
          revenue,
          tickets,
          status: eventDate > new Date() ? 'upcoming' as const : revenue > 0 ? 'completed' as const : 'ongoing' as const,
        };
      })
      .filter((event) => event.revenue > 0 || event.tickets > 0)
  ), [events, reservationsState]);

  const expenseBreakdown = useMemo(() => {
    if (totalRevenue <= 0) {
      return [];
    }

    return [
      { category: 'Logistique', amount: Math.round(totalRevenue * 0.12), fill: '#1B2A4A' },
      { category: 'Communication', amount: Math.round(totalRevenue * 0.08), fill: '#C9A84C' },
      { category: 'Billetterie', amount: Math.round(totalRevenue * 0.05), fill: '#10B981' },
    ].filter((item) => item.amount > 0);
  }, [totalRevenue]);

  const bestMonth = ticketsMonthly.reduce((best, current) => current.revenus > best.revenus ? current : best, ticketsMonthly[0] ?? { month: '-', revenus: 0, tickets: 0 });
  const featuredEvent = [...events].sort((left, right) => right.soldSeats - left.soldSeats)[0];

  const statsCards = [
    { label: 'Revenus totaux', value: formatPrice(totalRevenue), icon: 'ri-money-cny-circle-line', color: 'from-gold to-gold-dark', sub: `+${formatPrice(currentMonthRevenue)} ce mois`, subColor: 'text-emerald-600' },
    { label: 'Tickets vendus', value: totalTicketsSold.toLocaleString('fr-FR'), icon: 'ri-ticket-line', color: 'from-navy to-navy-lighter', sub: `${ticketStatusCounts.pending} en attente`, subColor: 'text-gray-500' },
    { label: 'Événements', value: events.length, icon: 'ri-calendar-event-line', color: 'from-navy-lighter to-navy-light', sub: `${events.filter(e => e.status === 'pending').length} en attente`, subColor: 'text-gold' },
    { label: 'Taux remplissage', value: `${avgFillRate}%`, icon: 'ri-pie-chart-line', color: 'from-gold-dark to-gold', sub: 'Moyenne événements', subColor: 'text-gray-500' },
  ];

  const chartHighlights = [
    { label: 'Meilleur mois', value: bestMonth.month, sub: `${bestMonth.tickets} tickets`, icon: 'ri-trophy-line', color: 'from-gold to-gold-dark' },
    { label: 'Événement phare', value: featuredEvent?.title ?? 'Aucun', sub: featuredEvent ? `${featuredEvent.totalSeats} places` : 'Pas encore de ventes', icon: 'ri-calendar-event-line', color: 'from-navy to-navy-light' },
    { label: 'Taux moyen', value: `${avgFillRate}%`, sub: 'Remplissage global', icon: 'ri-pie-chart-line', color: 'from-emerald-500 to-teal-600' },
    { label: 'Revenus ce mois', value: formatPrice(currentMonthRevenue), sub: `${ticketStatusCounts.confirmed} tickets confirmés`, icon: 'ri-money-cny-circle-line', color: 'from-amber-500 to-yellow-500' },
  ];

  const setField = (k: keyof EventForm, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const setTicketTypeField = (index: number, field: keyof TicketTypeForm, value: string) => {
    setForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.map((ticketType, itemIndex) => (
        itemIndex === index ? { ...ticketType, [field]: value } : ticketType
      )),
    }));
  };

  const addTicketType = () => {
    setForm((current) => ({
      ...current,
      ticketTypes: [...current.ticketTypes, { name: '', price: '0', seats: '', description: '' }],
    }));
  };

  const removeTicketType = (index: number) => {
    setForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.length > 1
        ? current.ticketTypes.filter((_, itemIndex) => itemIndex !== index)
        : current.ticketTypes,
    }));
  };

  const setEditTicketTypeField = (index: number, field: keyof TicketTypeForm, value: string) => {
    setEditEventForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.map((ticketType, itemIndex) => (
        itemIndex === index ? { ...ticketType, [field]: value } : ticketType
      )),
    }));
  };

  const addEditTicketType = () => {
    setEditEventForm((current) => ({
      ...current,
      ticketTypes: [...current.ticketTypes, { name: '', price: '0', seats: '', description: '' }],
    }));
  };

  const removeEditTicketType = (index: number) => {
    setEditEventForm((current) => ({
      ...current,
      ticketTypes: current.ticketTypes.length > 1
        ? current.ticketTypes.filter((_, itemIndex) => itemIndex !== index)
        : current.ticketTypes,
    }));
  };

  const handleEventImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEventImageFile(file);
    setEventImagePreview(preview);
  };

  const handleEditEventImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEditEventImageFile(file);
    setEditEventForm((current) => ({ ...current, image: preview }));
  };

  const validateStep1 = () => {
    const e: typeof errors = {};
    if (form.title.length < 3) e.title = 'Minimum 3 caractères';
    if (!form.type) e.type = 'Requis';
    if (!form.date) e.date = 'Requis';
    else if (new Date(form.date) <= new Date()) e.date = 'La date doit être dans le futur';
    if (!form.location) e.location = 'Requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: typeof errors = {};
    const ticketTypes = normalizedTicketTypes(form.ticketTypes);
    if (ticketTypes.length === 0) {
      e.ticketTypes = 'Ajoutez au moins un tarif';
    }
    if (form.ticketTypes.some((ticketType) => Number(ticketType.price) < 0)) {
      e.ticketTypes = 'Prix ≥ 0';
    }
    if (form.ticketTypes.some((ticketType) => Number(ticketType.price) > 0 && (!ticketType.seats || Number(ticketType.seats) < 1))) {
      e.ticketTypes = 'Chaque tarif payant doit avoir au moins 1 place';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    const summary = ticketSummary(form.ticketTypes);
    const newEvent: Event & { imageFile?: File | null } = {
      id: `oe-${Date.now()}`,
      title: form.title,
      type: form.type,
      date: form.date,
      endDate: form.endDate || form.date,
      location: form.location,
      diocese: form.diocese.trim(),
      description: form.description,
      ticketPrice: summary.ticketPrice,
      totalSeats: isPaidEvent ? summary.totalSeats : 0,
      ticketTypes: summary.ticketTypes,
      soldSeats: 0,
      status: 'pending',
      organizerId: String(user?.id ?? ''),
      organizerName: form.organizerName,
      image: eventImagePreview || 'https://readdy.ai/api/search-image?query=event%20celebration%20festival%20stage%20lights%20crowd%20music%20africa%20colorful%20vibrant%20atmosphere%20night&width=400&height=250&seq=new-oe&orientation=landscape',
      createdAt: new Date().toISOString(),
      contact: form.contact,
      imageFile: eventImageFile,
    };
    setSubmitSaving(true);
    setSubmitError('');

    try {
      await addEvent(newEvent);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setStep(1);
        setForm(emptyForm);
        setEventImageFile(null);
        setEventImagePreview('');
        setTab('events');
      }, 2500);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Impossible de créer l'événement.");
    } finally {
      setSubmitSaving(false);
    }
  };

  function fillRate(sold: number, total: number) {
    return total === 0 ? 0 : Math.round((sold / total) * 100);
  }

  const openEditEvent = (event: Event) => {
    setEditEvent(event);
    setEditEventForm({
      title: event.title,
      type: event.type,
      date: event.date,
      endDate: event.endDate,
      location: event.location,
      diocese: event.diocese ?? '',
      ticketPrice: String(event.ticketPrice),
      totalSeats: String(event.totalSeats),
      ticketTypes: event.ticketTypes?.length
        ? event.ticketTypes.map((ticketType) => ({
            id: ticketType.id,
            name: ticketType.name,
            price: String(ticketType.price),
            seats: ticketType.seats == null ? '' : String(ticketType.seats),
            description: ticketType.description ?? '',
          }))
        : [{ name: 'Standard', price: String(event.ticketPrice), seats: String(event.totalSeats || ''), description: '' }],
      image: event.image,
      description: event.description,
    });
    setEditEventImageFile(null);
  };

  const handleSaveEventEdit = async () => {
    if (!editEvent) return;

    const summary = ticketSummary(editEventForm.ticketTypes);
    await updateEvent(editEvent.id, {
      ...editEvent,
      title: editEventForm.title,
      type: editEventForm.type,
      date: editEventForm.date,
      endDate: editEventForm.endDate || editEventForm.date,
      location: editEventForm.location,
      diocese: editEventForm.diocese.trim(),
      ticketPrice: summary.ticketPrice,
      totalSeats: isEditPaidEvent ? summary.totalSeats : 0,
      ticketTypes: summary.ticketTypes,
      image: editEventForm.image,
      imageFile: editEventImageFile,
      description: editEventForm.description,
      status: 'pending',
    });

    setEditEvent(null);
  };

  const handleDeleteEvent = async () => {
    if (!deleteEventTarget) return;
    await deleteEvent(deleteEventTarget.id);
    if (selectedEvent?.id === deleteEventTarget.id) {
      setSelectedEvent(null);
    }
    setDeleteEventTarget(null);
  };

  const handleRestoreEvent = async (event: Event) => {
    await restoreEvent(event.id);
  };

  // ── Profile handlers ──
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      setProfileEdit((p) => ({ ...p, avatar: url }));
      setProfile((p) => ({ ...p, avatar: url }));
    };
    reader.readAsDataURL(file);
  };

  const handleProfileSave = async () => {
    if (!profileEdit.name.trim()) {
      setProfileToast('error:Nom obligatoire');
      setTimeout(() => setProfileToast(null), 3000);
      return;
    }
    setProfileSaving(true);

    try {
      const body = new FormData();
      body.append('_method', 'PUT');
      body.append('name', profileEdit.name);
      body.append('email', profileEdit.email);
      body.append('phone', profileEdit.phone);
      body.append('structure_name', profileEdit.structureName);
      body.append('paroisse', profileEdit.location);
      body.append('diocese_prefere', profileEdit.location);
      body.append('category', profileEdit.eventCategory);
      body.append('bio', profileEdit.bio);
      if (profileAvatarFile) body.append('avatar_file', profileAvatarFile);

      const updatedUser = await apiRequest<NonNullable<typeof user>>('/user', {
        method: 'POST',
        body,
      });

      updateUser(updatedUser);
      const nextProfile = profileFromUser(updatedUser);
      setProfile(nextProfile);
      setProfileEdit(nextProfile);
      setProfileAvatarFile(null);
      setProfileSaving(false);
      setProfileToast('success:Profil mis à jour avec succès');
      setTimeout(() => setProfileToast(null), 3000);
    } catch (error) {
      setProfileSaving(false);
      setProfileToast(`error:${error instanceof Error ? error.message : 'Mise à jour du profil impossible'}`);
      setTimeout(() => setProfileToast(null), 3000);
    }
  };

  const handleProfileCancel = () => {
    setProfileEdit({ ...profile });
    setProfileAvatarFile(null);
    setProfileToast('info:Modifications annulées');
    setTimeout(() => setProfileToast(null), 3000);
  };

  const setProfileField = (k: keyof ProfileData, v: string) =>
    setProfileEdit((p) => ({ ...p, [k]: v }));

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-cream md:flex-row">
      <DashboardSidebar
        role="organizer"
        userName={profile.name}
        userAvatar={profile.avatar}
        badgeCounts={{ '/organizer/notifications': unreadCount }}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <DashboardTopBar
          title="Dashboard Organisateur"
          subtitle={`Bienvenue, ${profile.name}`}
          userName={profile.name}
          userAvatar={profile.avatar}
          notificationRole="organizer"
        />

        {/* New notification toast */}
        {newNotifToast && (
          <div className="fixed top-5 right-5 z-50 max-w-sm w-full animate-fade-in">
            <div className={`flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium
              ${newNotifToast.includes('approuvé')
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0
                ${newNotifToast.includes('approuvé') ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <i className={newNotifToast.includes('approuvé') ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-close-circle-line text-red-500'}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-xs mb-0.5">
                  {newNotifToast.includes('approuvé') ? 'Événement approuvé !' : 'Événement rejeté'}
                </p>
                <p className="text-xs opacity-80 leading-relaxed whitespace-pre-line break-words">{newNotifToast}</p>
              </div>
              <button onClick={() => setNewNotifToast(null)} className="flex-shrink-0 w-5 h-5 flex items-center justify-center opacity-60 hover:opacity-100 cursor-pointer">
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-6 sm:p-6">

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statsCards.map((c, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-cream-dark">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${c.color} mb-3`}>
                      <i className={`${c.icon} text-white text-lg`}></i>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{c.label}</p>
                    <p className="text-xl font-bold text-navy">{c.value}</p>
                    <p className={`text-xs mt-1 ${c.subColor}`}>{c.sub}</p>
                  </div>
                ))}
              </div>
              <OrganizerCharts
                ticketsMonthly={ticketsMonthly}
                fillRate={fillRateByEvent}
                revenueByType={revenueByType}
                highlights={chartHighlights}
              />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-cream-dark p-5">
                  <h3 className="text-sm font-bold text-navy mb-4">Mes événements</h3>
                  <div className="space-y-3">
                    {events.map((ev) => (
                      <div key={ev.id} className="flex items-center gap-3 hover:bg-cream/50 rounded-lg px-1 py-0.5 cursor-pointer transition-colors" onClick={() => setSelectedEvent(ev)}>
                        <img src={ev.image} alt={ev.title} className="w-14 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-navy truncate">{ev.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex-1 bg-cream-dark rounded-full h-1.5">
                              <div className="bg-gold h-1.5 rounded-full transition-all" style={{ width: `${fillRate(ev.soldSeats, ev.totalSeats)}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{ev.soldSeats}/{ev.totalSeats}</span>
                          </div>
                        </div>
                        <StatusBadge status={ev.status} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-cream-dark p-5">
                  <h3 className="text-sm font-bold text-navy mb-4">Réservations récentes</h3>
                  <div className="space-y-3">
                    {reservationsState.slice(0, 4).map((r) => (
                      <div key={r.id} className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-gold-100 rounded-full flex-shrink-0">
                          <i className="ri-user-line text-gold text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-navy">{r.buyer}</p>
                          <p className="text-xs text-gray-400 truncate">{r.eventTitle} · {r.tickets} ticket(s)</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-navy">{formatPrice(r.total)}</p>
                          <span className="text-xs text-gray-400">{new Date(r.date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── EVENTS ── */}
          {tab === 'events' && (
            <div className="space-y-4">

              {/* Stat cards par statut */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {([
                  { key: 'all' as const,      label: 'Tous',        icon: 'ri-calendar-event-line', activeColor: 'bg-navy text-white',     inactiveColor: 'bg-white text-navy border border-cream-dark',     countColor: 'text-navy',       subColor: 'text-gray-400' },
                  { key: 'pending' as const,   label: 'En attente de validation',  icon: 'ri-time-line',           activeColor: 'bg-gold text-white',     inactiveColor: 'bg-white text-gold-dark border border-cream-dark', countColor: 'text-gold-dark',   subColor: 'text-gold/70' },
                  { key: 'approved' as const,  label: 'Validés par l’admin',   icon: 'ri-checkbox-circle-line', activeColor: 'bg-emerald-500 text-white', inactiveColor: 'bg-white text-emerald-600 border border-cream-dark', countColor: 'text-emerald-600', subColor: 'text-emerald-400' },
                  { key: 'rejected' as const,  label: 'Rejetés',     icon: 'ri-close-circle-line',   activeColor: 'bg-red-500 text-white',  inactiveColor: 'bg-white text-red-500 border border-cream-dark',  countColor: 'text-red-500',    subColor: 'text-red-300' },
                  { key: 'deleted' as const,   label: 'Corbeille',   icon: 'ri-delete-bin-line',     activeColor: 'bg-slate-600 text-white', inactiveColor: 'bg-white text-slate-500 border border-cream-dark', countColor: 'text-slate-500', subColor: 'text-slate-300' },
                ]).map((s) => {
                  const isActive = eventStatusFilter === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setEventStatusFilter(s.key)}
                      className={`rounded-xl p-4 text-left transition-all cursor-pointer ${isActive ? s.activeColor : s.inactiveColor}`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-2 ${isActive ? 'bg-white/20' : 'bg-cream'}`}>
                        <i className={`${s.icon} text-base ${isActive ? 'text-white' : s.countColor}`}></i>
                      </div>
                      <p className={`text-2xl font-bold leading-tight ${isActive ? 'text-white' : s.countColor}`}>{eventStatusCounts[s.key]}</p>
                      <p className={`text-xs mt-0.5 font-medium ${isActive ? 'text-white/75' : 'text-gray-500'}`}>{s.label}</p>
                    </button>
                  );
                })}
              </div>

              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-navy">
                  Mes Événements
                  <span className="ml-1.5 bg-gold-100 text-gold text-xs px-2 py-0.5 rounded-full">{filteredEvents.length}/{events.length}</span>
                </h3>
                <button onClick={() => setTab('add')} className="flex items-center gap-1.5 px-4 py-2 bg-gold text-white text-sm rounded-xl hover:bg-gold-dark cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i>Ajouter
                </button>
              </div>

              {/* Search bar */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-sm"></i>
                </div>
                <input
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder="Rechercher par titre, type ou lieu…"
                  className="w-full pl-9 pr-9 py-2.5 bg-white border border-cream-dark rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                />
                {eventSearch && (
                  <button onClick={() => setEventSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>

              {filteredEvents.length === 0 && (
                <div className="bg-white rounded-xl border border-cream-dark py-12 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-cream rounded-full mx-auto mb-3">
                    <i className="ri-search-line text-gray-400 text-xl"></i>
                  </div>
                  <p className="text-sm font-semibold text-gray-500">Aucun événement trouvé</p>
                  <p className="text-xs text-gray-400 mt-1">Essayez un autre terme ou changez le filtre</p>
                  <button onClick={() => { setEventSearch(''); setEventStatusFilter('all'); }} className="mt-3 text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
              {filteredEvents.map((ev) => (
                <div
                  key={ev.id}
                  className="bg-white rounded-xl border border-cream-dark overflow-hidden hover:border-gold/50 hover:bg-gold-50/10 transition-all cursor-pointer"
                  onClick={() => setSelectedEvent(ev)}
                >
                  <div className="flex items-stretch">
                    <img src={ev.image} alt={ev.title} className="w-40 object-cover bg-gray-100 flex-shrink-0" />
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs bg-gold-100 text-gold-dark px-2 py-0.5 rounded-full font-medium">{ev.type}</span>
                            <StatusBadge status={ev.status} />
                          </div>
                          <h4 className="text-base font-bold text-navy">{ev.title}</h4>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{ev.location}</span>
                            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{new Date(ev.date).toLocaleDateString('fr-FR')}</span>
                            <span className="flex items-center gap-1"><i className="ri-ticket-line"></i>{formatPrice(ev.ticketPrice)}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-navy">{ev.soldSeats}<span className="text-sm text-gray-400">/{ev.totalSeats}</span></p>
                          <p className="text-xs text-gray-400">places vendues</p>
                          <div className="w-24 bg-cream-dark rounded-full h-1.5 mt-1 ml-auto">
                            <div className="bg-gold h-1.5 rounded-full" style={{ width: `${fillRate(ev.soldSeats, ev.totalSeats)}%` }}></div>
                          </div>
                          <p className="text-xs text-gold font-semibold mt-0.5">{fillRate(ev.soldSeats, ev.totalSeats)}% rempli</p>
                        </div>
                      </div>
                      {ev.rejectionReason && (
                        <div className="mt-3 bg-red-50 rounded-lg p-2.5 border border-red-100">
                          <p className="text-xs text-red-600"><strong>Rejet :</strong> {ev.rejectionReason}</p>
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-end gap-2">
                        {ev.status === 'deleted' ? (
                          <button
                            onClick={(event) => { event.stopPropagation(); void handleRestoreEvent(ev); }}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
                            title="Restaurer"
                          >
                            <i className="ri-arrow-go-back-line text-sm"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={(event) => { event.stopPropagation(); openEditEvent(ev); }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gold-100 text-gold-dark transition-colors cursor-pointer"
                              title="Modifier"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={(event) => { event.stopPropagation(); setDeleteEventTarget(ev); }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-500 transition-colors cursor-pointer"
                              title="Mettre dans la corbeille"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </>
                        )}
                        <span className="text-xs text-gold font-semibold flex items-center gap-1">
                          Voir les détails <i className="ri-arrow-right-s-line"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── TICKETS ── */}
          {tab === 'tickets' && (
            <div className="space-y-4">

              {/* Stat cards par statut */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {([
                  { key: 'all' as const,       label: 'Tous',        icon: 'ri-ticket-line',          activeColor: 'bg-navy text-white',        inactiveColor: 'bg-white text-navy border border-cream-dark',        countColor: 'text-navy' },
                  { key: 'confirmed' as const,  label: 'Confirmés',   icon: 'ri-checkbox-circle-line', activeColor: 'bg-emerald-500 text-white',  inactiveColor: 'bg-white text-emerald-600 border border-cream-dark', countColor: 'text-emerald-600' },
                  { key: 'pending' as const,    label: 'Paiement non validé',  icon: 'ri-time-line',            activeColor: 'bg-gold text-white',        inactiveColor: 'bg-white text-gold-dark border border-cream-dark',  countColor: 'text-gold-dark' },
                  { key: 'cancelled' as const,  label: 'Annulés',     icon: 'ri-close-circle-line',    activeColor: 'bg-red-500 text-white',     inactiveColor: 'bg-white text-red-500 border border-cream-dark',    countColor: 'text-red-500' },
                ]).map((s) => {
                  const isActive = ticketStatusFilter === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setTicketStatusFilter(s.key)}
                      className={`rounded-xl p-4 text-left transition-all cursor-pointer ${isActive ? s.activeColor : s.inactiveColor}`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-2 ${isActive ? 'bg-white/20' : 'bg-cream'}`}>
                        <i className={`${s.icon} text-base ${isActive ? 'text-white' : s.countColor}`}></i>
                      </div>
                      <p className={`text-2xl font-bold leading-tight ${isActive ? 'text-white' : s.countColor}`}>{ticketStatusCounts[s.key]}</p>
                      <p className={`text-xs mt-0.5 font-medium ${isActive ? 'text-white/75' : 'text-gray-500'}`}>{s.label}</p>
                    </button>
                  );
                })}
              </div>

              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-navy">
                  Réservations &amp; Tickets
                  <span className="ml-1.5 bg-gold-100 text-gold text-xs px-2 py-0.5 rounded-full">{filteredTickets.length}/{reservationsState.length}</span>
                </h3>
              </div>

              {/* Search bar */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-sm"></i>
                </div>
                <input
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                  placeholder="Rechercher par code, acheteur, événement…"
                  className="w-full pl-9 pr-9 py-2.5 bg-white border border-cream-dark rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                />
                {ticketSearch && (
                  <button onClick={() => setTicketSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>

              <div className="bg-white rounded-xl border border-cream-dark">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-cream">
                      {['Code ticket', 'Acheteur', 'Événement', 'Tickets', 'Total', 'Statut', 'Date', ''].map((h) => (
                        <th key={h} className="text-left text-xs font-semibold text-gray-400 px-5 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-12 text-center">
                          <div className="w-10 h-10 flex items-center justify-center bg-cream rounded-full mx-auto mb-3">
                            <i className="ri-ticket-line text-gray-400 text-lg"></i>
                          </div>
                          <p className="text-sm font-semibold text-gray-500">Aucun ticket trouvé</p>
                          <p className="text-xs text-gray-400 mt-1">Essayez un autre terme ou changez le filtre</p>
                          <button onClick={() => { setTicketSearch(''); setTicketStatusFilter('all'); }} className="mt-3 text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">
                            Réinitialiser
                          </button>
                        </td>
                      </tr>
                    ) : filteredTickets.map((r) => (
                      <tr key={r.id} className="border-b border-cream hover:bg-gold-50/30 transition-colors cursor-pointer" onClick={() => setSelectedTicket(r)}>
                        <td className="px-5 py-3 font-mono text-xs text-gold font-semibold">{r.ticketCode}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-navy">{r.buyer}</p>
                          <p className="text-xs text-gray-400">{r.email}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[140px] truncate">{r.eventTitle}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-navy">{r.tickets}</td>
                        <td className="px-4 py-3 text-sm font-bold text-navy">{formatPrice(r.total)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${r.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : r.status === 'pending' ? 'bg-gold-100 text-gold-dark' : 'bg-gray-100 text-gray-600'}`}>
                            {r.status === 'confirmed' ? 'Confirmé' : r.status === 'pending' ? 'Paiement non validé' : 'Annulé'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">{formatDate(r.date)}</td>
                        <td className="px-3 py-3">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-cream hover:bg-gold/10 transition-colors">
                            <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}

          {/* ── FINANCES ── */}
          {tab === 'finances' && (
            <FinancialDashboard
              monthlyComparison={ticketsMonthly}
              revenueByEvent={revenueByEvent}
              revenueByType={revenueByType}
              expenseBreakdown={expenseBreakdown}
              onMoveToTrash={(eventId) => {
                const event = events.find((item) => item.id === eventId);
                if (event) setDeleteEventTarget(event);
              }}
            />
          )}

          {/* ── NOTIFICATIONS ── */}
          {tab === 'notifications' && (
            <div className="bg-white rounded-xl border border-cream-dark">
              <div className="flex items-center justify-between px-5 py-4 border-b border-cream-dark">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-navy">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button onClick={() => { markAllAsRead(); saveLastSeenDate(); }} className="text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              <div className="divide-y divide-cream">
                {notifications.map((n) => {
                  const iconMap: Record<string, string> = {
                    order: 'ri-shopping-bag-line',
                    approval: 'ri-checkbox-circle-line',
                    rejection: 'ri-close-circle-line',
                    reservation: 'ri-ticket-line',
                  };
                  const colorMap: Record<string, string> = {
                    order: 'text-gold bg-gold-100',
                    approval: 'text-emerald-500 bg-emerald-50',
                    rejection: 'text-red-500 bg-red-50',
                    reservation: 'text-navy bg-navy/10',
                  };
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        markAsRead(n.id);
                        setSelectedNotification({ ...n, read: true });
                      }}
                      className={`flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-cream/50 transition-colors ${!n.read ? 'bg-gold-50/40' : ''}`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${colorMap[n.type]}`}>
                        <i className={`${iconMap[n.type]} text-base`}></i>
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${!n.read ? 'text-navy' : 'text-gray-600'}`}>{n.title}</p>
                        <p className="text-sm text-gray-500 mt-0.5 whitespace-pre-line break-words">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 bg-gold rounded-full mt-2 flex-shrink-0"></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── ADD EVENT ── */}
          {tab === 'add' && (
            <div className="max-w-2xl mx-auto">
              {submitted ? (
                <div className="bg-white rounded-2xl p-10 border border-cream-dark text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-gold-100 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-gold text-3xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2">Événement soumis avec succès !</h3>
                  <p className="text-sm text-gray-500">Votre événement est en cours de validation par l&apos;administrateur.<br />Vous serez notifié dès qu&apos;il sera approuvé.</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 bg-gold-100 text-gold-dark rounded-full text-sm font-semibold">
                    <i className="ri-time-line text-xs"></i>🟡 En attente de validation
                  </span>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-cream-dark overflow-hidden">
                  {/* Steps */}
                  <div className="px-6 py-4 border-b border-cream-dark">
                    <div className="flex items-center gap-3">
                      {[{ n: 1, l: 'Détails' }, { n: 2, l: 'Billetterie' }, { n: 3, l: 'Aperçu' }].map((s, i, arr) => (
                        <div key={s.n} className="flex items-center gap-2 flex-1">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${step >= s.n ? 'bg-gold text-white' : 'bg-cream-dark text-gray-400'}`}>
                            {step > s.n ? <i className="ri-check-line text-xs"></i> : s.n}
                          </div>
                          <span className={`text-xs font-medium whitespace-nowrap ${step >= s.n ? 'text-navy' : 'text-gray-400'}`}>{s.l}</span>
                          {i < arr.length - 1 && <div className={`flex-1 h-0.5 ${step > s.n ? 'bg-gold' : 'bg-cream-dark'}`}></div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    {step === 1 && (
                      <>
                        <h3 className="text-sm font-bold text-navy mb-2">Détails de l&apos;événement</h3>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Titre de l&apos;événement *</label>
                          <input value={form.title} onChange={(e) => setField('title', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: AfroFest 2026 – Grand Concert" />
                          {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie *</label>
                          <select value={form.type} onChange={(e) => setField('type', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                            <option value="">Choisir une catégorie</option>
                            {EVENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                          </select>
                          {errors.type && <p className="text-xs text-red-500 mt-1">{errors.type}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Date &amp; heure de début *</label>
                            <input type="datetime-local" value={form.date} onChange={(e) => setField('date', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                            {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Date de fin</label>
                            <input type="datetime-local" value={form.endDate} onChange={(e) => setField('endDate', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Lieu *</label>
                          <input value={form.location} onChange={(e) => setField('location', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Grand Théâtre de Dakar" />
                          {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Diocèse</label>
                          <input value={form.diocese} onChange={(e) => setField('diocese', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Dakar" />
                          {errors.diocese && <p className="text-xs text-red-500 mt-1">{errors.diocese}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                          <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24" placeholder="Décrivez votre événement..." />
                          <div className="flex justify-between items-center mt-1">
                            {errors.description ? <p className="text-xs text-red-500">{errors.description}</p> : <span />}
                          </div>
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <h3 className="text-sm font-bold text-navy mb-2">Billetterie &amp; Contact</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-600">Types de tickets *</p>
                              <p className="text-xs text-gray-400">Ex: Standard, VIP, Couple, Enfant</p>
                            </div>
                            <button type="button" onClick={addTicketType} className="inline-flex items-center gap-1.5 rounded-xl border border-cream-dark px-3 py-2 text-xs font-semibold text-navy hover:bg-cream">
                              <i className="ri-add-line"></i> Ajouter un tarif
                            </button>
                          </div>
                          {form.ticketTypes.map((ticketType, index) => (
                            <div key={index} className="rounded-xl border border-cream-dark bg-cream/30 p-3">
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr_1fr_auto]">
                                <input value={ticketType.name} onChange={(e) => setTicketTypeField(index, 'name', e.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Nom du tarif" />
                                <input type="number" value={ticketType.price} onChange={(e) => setTicketTypeField(index, 'price', e.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Prix FCFA" />
                                <input type="number" value={ticketType.seats} onChange={(e) => setTicketTypeField(index, 'seats', e.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Places" />
                                <button type="button" onClick={() => removeTicketType(index)} disabled={form.ticketTypes.length === 1} className="flex h-10 w-10 items-center justify-center rounded-xl text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30">
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                              <input value={ticketType.description} onChange={(e) => setTicketTypeField(index, 'description', e.target.value)} className="mt-3 w-full rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Description courte optionnelle" />
                            </div>
                          ))}
                          {errors.ticketTypes && <p className="text-xs text-red-500">{errors.ticketTypes}</p>}
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                            Prix affiché : {formatPrice(formTicketSummary.ticketPrice)} · Places totales : {formTicketSummary.totalSeats || 'illimitées / non renseignées'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Nom de l'organisation</label>
                          <input value={form.organizerName} onChange={(e) => setField('organizerName', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Nom de l'organisation" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Contact (tél/email)</label>
                          <input value={form.contact} onChange={(e) => setField('contact', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="+237 699 xxx xxx" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Lien externe (optionnel)</label>
                          <input value={form.externalLink} onChange={(e) => setField('externalLink', e.target.value)} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="https://..." />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Affiche de l'événement</label>
                          <input ref={eventImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleEventImageChange} />
                          <div className="flex items-center gap-3">
                            {eventImagePreview ? <img src={eventImagePreview} alt="" className="w-20 h-20 rounded-xl object-cover border border-cream-dark" /> : <div className="w-20 h-20 rounded-xl border border-dashed border-cream-dark bg-cream flex items-center justify-center text-gray-400"><i className="ri-image-line text-xl"></i></div>}
                            <button type="button" onClick={() => eventImageInputRef.current?.click()} className="px-4 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap">
                              Choisir une image
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <h3 className="text-sm font-bold text-navy mb-4">Aperçu de votre événement</h3>
                        <div className="bg-cream rounded-xl p-4 border border-cream-dark space-y-3">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs bg-gold-100 text-gold-dark px-2 py-0.5 rounded-full font-medium">{form.type || 'Type'}</span>
                            </div>
                            {eventImagePreview ? <img src={eventImagePreview} alt="" className="w-full h-40 rounded-xl object-cover mb-3 bg-gray-100" /> : null}
                            <h4 className="text-base font-bold text-navy">{form.title || 'Titre de l\'événement'}</h4>
                            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1"><i className="ri-map-pin-line"></i>{form.location || '—'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { l: 'Date début', v: form.date ? new Date(form.date).toLocaleDateString('fr-FR') : '—' },
                              { l: 'Date fin', v: form.endDate ? new Date(form.endDate).toLocaleDateString('fr-FR') : '—' },
                              { l: 'Diocèse', v: form.diocese || '—' },
                              { l: 'Prix ticket', v: formTicketSummary.ticketPrice === 0 ? 'Gratuit' : `À partir de ${formatPrice(formTicketSummary.ticketPrice)}` },
                              { l: 'Places', v: formTicketSummary.totalSeats ? String(formTicketSummary.totalSeats) : 'Entree libre' },
                              { l: "Nom de l'organisation", v: form.organizerName || '—' },
                              { l: 'Contact', v: form.contact || '—' },
                            ].map((item) => (
                              <div key={item.l} className="bg-white rounded-lg p-2.5">
                                <p className="text-xs text-gray-400">{item.l}</p>
                                <p className="text-sm font-semibold text-navy">{item.v}</p>
                              </div>
                            ))}
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-400 mb-1">Description</p>
                            <p className="text-sm text-gray-700">{form.description || '—'}</p>
                          </div>
                          <div className="flex items-center gap-2 bg-gold-100/60 p-3 rounded-xl">
                            <i className="ri-information-line text-gold"></i>
                            <p className="text-xs text-gold-dark">Après soumission, votre événement sera examiné par l&apos;admin avant publication.</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {submitError && (
                    <div className="mx-6 mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
                      {submitError}
                    </div>
                  )}

                  <div className="px-6 pb-6 flex items-center gap-3">
                    {step > 1 && (
                      <button onClick={() => setStep((s) => (s - 1) as Step)} className="px-5 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap">
                        ← Retour
                      </button>
                    )}
                    <div className="flex-1"></div>
                    {step < 3
                      ? <button onClick={handleNext} className="px-6 py-2.5 bg-gold text-white rounded-xl text-sm font-semibold hover:bg-gold-dark cursor-pointer whitespace-nowrap">Continuer →</button>
                      : <button onClick={() => void handleSubmit()} disabled={submitSaving} className="flex items-center gap-2 px-6 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-lighter disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"><i className={submitSaving ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>{submitSaving ? 'Envoi...' : 'Soumettre pour validation'}</button>
                    }
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PROFILE ── */}
          {tab === 'profile' && (
            <div className="max-w-2xl mx-auto space-y-5">
              {/* Toast */}
              {profileToast && (
                <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-semibold animate-fade-in ${
                  profileToast.startsWith('success') ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                  : profileToast.startsWith('error') ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-gold-100 border border-gold-100 text-gold-dark'
                }`}>
                  <i className={profileToast.startsWith('success') ? 'ri-checkbox-circle-line' : profileToast.startsWith('error') ? 'ri-error-warning-line' : 'ri-information-line'}></i>
                  {profileToast.split(':')[1]}
                </div>
              )}

              {/* Avatar card */}
              <div className="bg-white rounded-xl border border-cream-dark p-6">
                <div className="flex items-center gap-5">
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gold">
                      {profile.avatar ? (
                        <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-navy text-xl font-bold text-gold">
                          {(profile.name || 'O').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-gold rounded-full cursor-pointer hover:bg-gold-dark transition-colors"
                    >
                      <i className="ri-camera-line text-white text-xs"></i>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-bold text-navy">{profile.name}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{profile.structureName}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-gold-100 text-gold-dark px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <i className="ri-calendar-event-fill text-xs"></i>Organisateur
                      </span>
                      <span className="text-xs bg-cream text-gray-500 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-map-pin-line text-xs"></i>{profile.location}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-4 text-center flex-shrink-0">
                    <div>
                      <p className="text-lg font-bold text-navy">{events.length}</p>
                      <p className="text-xs text-gray-400">Événements</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-navy">{totalTicketsSold.toLocaleString('fr-FR')}</p>
                      <p className="text-xs text-gray-400">Tickets</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal info */}
              <div className="bg-white rounded-xl border border-cream-dark p-6">
                <h3 className="text-sm font-bold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-user-line text-gold"></i>Informations personnelles
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Nom complet *</label>
                    <input
                      value={profileEdit.name}
                      onChange={(e) => setProfileField('name', e.target.value)}
                      className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Adresse e-mail</label>
                    <input
                      value={profileEdit.email}
                      onChange={(e) => setProfileField('email', e.target.value)}
                      className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Téléphone</label>
                    <input
                      value={profileEdit.phone}
                      onChange={(e) => setProfileField('phone', e.target.value)}
                      className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Localisation</label>
                    <input
                      value={profileEdit.location}
                      onChange={(e) => setProfileField('location', e.target.value)}
                      className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                      placeholder="Ville, Pays"
                    />
                  </div>
                </div>
              </div>

              {/* Structure info */}
              <div className="bg-white rounded-xl border border-cream-dark p-6">
                <h3 className="text-sm font-bold text-navy mb-4 flex items-center gap-2">
                  <i className="ri-building-line text-gold"></i>Ma Structure
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Nom de la structure</label>
                      <input
                        value={profileEdit.structureName}
                        onChange={(e) => setProfileField('structureName', e.target.value)}
                        className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                        placeholder="Ex: Nkosi Events Production"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Catégorie principale</label>
                      <select
                        value={profileEdit.eventCategory}
                        onChange={(e) => setProfileField('eventCategory', e.target.value)}
                        className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                      >
                        <option value="">Proposition</option>
                        {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Bio / Description</label>
                    <textarea
                      value={profileEdit.bio}
                      onChange={(e) => setProfileField('bio', e.target.value)}
                      className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24"
                      placeholder="Décrivez votre structure et vos spécialités..."
                      maxLength={300}
                    />
                    <p className="text-xs text-gray-400 text-right mt-1">{profileEdit.bio.length}/300</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pb-4">
                <button
                  onClick={handleProfileCancel}
                  className="px-6 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  onClick={handleProfileSave}
                  disabled={profileSaving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gold text-white rounded-xl text-sm font-semibold hover:bg-gold-dark cursor-pointer whitespace-nowrap disabled:opacity-70"
                >
                  {profileSaving ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</>
                  ) : (
                    <><i className="ri-save-line"></i>Enregistrer</>
                  )}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {editEvent && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditEvent(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-navy">Modifier l'événement</h4>
              <button onClick={() => setEditEvent(null)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={editEventForm.title} onChange={(event) => setEditEventForm((current) => ({ ...current, title: event.target.value }))} placeholder="Titre" className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <input value={editEventForm.type} onChange={(event) => setEditEventForm((current) => ({ ...current, type: event.target.value }))} placeholder="Type" className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <input type="datetime-local" value={editEventForm.date.slice(0, 16)} onChange={(event) => setEditEventForm((current) => ({ ...current, date: event.target.value }))} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <input type="datetime-local" value={editEventForm.endDate.slice(0, 16)} onChange={(event) => setEditEventForm((current) => ({ ...current, endDate: event.target.value }))} className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <input value={editEventForm.location} onChange={(event) => setEditEventForm((current) => ({ ...current, location: event.target.value }))} placeholder="Lieu" className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <input value={editEventForm.diocese} onChange={(event) => setEditEventForm((current) => ({ ...current, diocese: event.target.value }))} placeholder="Diocèse" className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
              <div className="col-span-2 space-y-3 rounded-2xl border border-cream-dark bg-cream/40 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Types de tickets</p>
                  <button type="button" onClick={addEditTicketType} className="flex items-center gap-1 rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white hover:bg-navy-light">
                    <i className="ri-add-line"></i> Ajouter
                  </button>
                </div>
                {editEventForm.ticketTypes.map((ticketType, index) => (
                  <div key={`${ticketType.id ?? 'edit'}-${index}`} className="rounded-xl border border-cream-dark bg-white p-3">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_110px_110px_40px]">
                      <input value={ticketType.name} onChange={(event) => setEditTicketTypeField(index, 'name', event.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Nom du tarif" />
                      <input type="number" value={ticketType.price} onChange={(event) => setEditTicketTypeField(index, 'price', event.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Prix FCFA" />
                      <input type="number" value={ticketType.seats} onChange={(event) => setEditTicketTypeField(index, 'seats', event.target.value)} className="rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Places" />
                      <button type="button" onClick={() => removeEditTicketType(index)} disabled={editEventForm.ticketTypes.length === 1} className="flex h-10 w-10 items-center justify-center rounded-xl text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                    <input value={ticketType.description} onChange={(event) => setEditTicketTypeField(index, 'description', event.target.value)} className="mt-2 w-full rounded-xl border border-cream-dark px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Description courte optionnelle" />
                  </div>
                ))}
                <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm font-semibold text-navy">
                  Prix affiché : {formatPrice(editTicketSummary.ticketPrice)} · Places totales : {editTicketSummary.totalSeats || 'illimitées / non renseignées'}
                </div>
              </div>
            </div>
            <input ref={editEventImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleEditEventImageChange} />
            <div className="mt-3 flex items-center gap-3">
              {editEventForm.image ? <img src={editEventForm.image} alt="" className="w-24 h-16 rounded-xl object-cover bg-gray-100" /> : <div className="w-24 h-16 rounded-xl border border-dashed border-cream-dark bg-cream flex items-center justify-center text-gray-400"><i className="ri-image-line text-xl"></i></div>}
              <button type="button" onClick={() => editEventImageInputRef.current?.click()} className="px-4 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap">
                Remplacer l'image
              </button>
            </div>
            <textarea value={editEventForm.description} onChange={(event) => setEditEventForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description" className="w-full border border-cream-dark rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24 mt-3" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditEvent(null)} className="flex-1 px-5 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap">
                Annuler
              </button>
              <button onClick={() => void handleSaveEventEdit()} className="flex-1 px-5 py-2.5 bg-gold text-white rounded-xl text-sm font-semibold hover:bg-gold-dark cursor-pointer whitespace-nowrap">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteEventTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDeleteEventTarget(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(event) => event.stopPropagation()}>
            <h4 className="text-sm font-bold text-navy mb-2">Supprimer cet événement ?</h4>
            <p className="text-sm text-gray-500 mb-4">L'événement <strong>{deleteEventTarget.title}</strong> sera déplacé dans la corbeille et ne sera plus visible dans l’application.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteEventTarget(null)} className="flex-1 px-5 py-2.5 border border-cream-dark rounded-xl text-sm text-gray-600 hover:bg-cream cursor-pointer whitespace-nowrap">
                Annuler
              </button>
              <button onClick={() => void handleDeleteEvent()} className="flex-1 px-5 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">
                Mettre en corbeille
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedNotification ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedNotification(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold-dark">Notification</p>
                <h3 className="mt-1 text-lg font-bold text-navy">{selectedNotification.title}</h3>
                <p className="mt-1 text-xs text-gray-400">{selectedNotification.time}</p>
              </div>
              <button type="button" onClick={() => setSelectedNotification(null)} className="rounded-xl p-2 text-gray-400 hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="mt-5 whitespace-pre-line break-words text-sm leading-7 text-gray-600">{selectedNotification.message}</p>
            <button type="button" onClick={() => setSelectedNotification(null)} className="mt-6 w-full rounded-xl bg-navy py-3 text-sm font-bold text-white">
              Fermer
            </button>
          </div>
        </div>
      ) : null}

      {/* Event Detail Panel */}
      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {/* Ticket Detail Panel */}
      {selectedTicket && (
        <TicketDetailPanel
          reservation={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </div>
  );
}
